import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import { type FlowLlmPromptBlock } from 'flow-schema';

import { Text, isDefined, isTruthy } from 'common';
import {
  LLM_MODELS,
  LlmModelModality,
  LlmModelName,
  LlmTemperatureSupport,
  LlmTopPSupport,
  isLlmModelOfferable,
} from 'llm-model-registry';

import { FileReferencesField } from './FileReferencesField';
import { LlmModelPickerField } from './LlmModelPickerField';
import { ResponseFormatFields } from './ResponseFormatFields';
import { TEMPERATURE_HELPER_TEXT } from './constants.ts';
import { BlockValueTemplateFieldArea } from '../../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockTextField } from '../../../ConfigFields/TextField';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField.ts';

/**
 * Flat list of selectable models, consumed by the simpler model selectors
 * (InformationExtraction, PriorAuthorization). The tiered Flow Builder picker
 * uses {@link LlmModelPickerField} instead.
 *
 * "Auto" (`notable_default`, the recommended default) always leads the list,
 * followed by the offerable concrete models. We pin it explicitly rather than
 * relying on registry insertion order.
 */
export const llmModelOptions = [
  LLM_MODELS[LlmModelName.notable_default],
  ...Object.values(LLM_MODELS).filter(
    (model) => model.id !== LlmModelName.notable_default && isLlmModelOfferable(model)
  ),
].map((model) => ({ label: model.name, value: model.id }));

interface SharedLlmBlockFormFieldsProps {
  fieldNamePrefix: string;
}

export function SharedLlmBlockFormFields(props: SharedLlmBlockFormFieldsProps) {
  const { fieldNamePrefix } = props;
  const { value: selectedModelId } = useSelectedNodeField<
    FlowLlmPromptBlock['config']['modelName']
  >(`${fieldNamePrefix}.modelName`);
  const selectedModel = isDefined(selectedModelId) ? LLM_MODELS[selectedModelId] : undefined;
  const isFileContextSupported = isTruthy(
    selectedModel?.inputModalities.includes(LlmModelModality.File)
  );
  const isTemperatureAndTopPSupported =
    !isDefined(selectedModel) ||
    (selectedModel.supportedParameters.temperature === LlmTemperatureSupport.Supported &&
      selectedModel.supportedParameters.topP === LlmTopPSupport.Supported);

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <BlockValueTemplateFieldArea
          label="Prompt"
          fieldName={`${fieldNamePrefix}.prompt`}
          helperText="Select Prompt to be given to the LLM. To pull upstream data, use {{upstreamBlockName.data}}."
        />
      </Stack>
      <LlmModelPickerField fieldNamePrefix={fieldNamePrefix} />
      <Stack>
        <Accordion variant="elevation">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Text variant="heading5">Model settings</Text>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              <BlockTextField
                label="Temperature"
                fieldName={`${fieldNamePrefix}.temperature`}
                type="number"
                slotProps={{ htmlInput: { min: 0, max: 2, step: 0.1 } }}
                disabled={!isTemperatureAndTopPSupported}
                helperText={
                  isTemperatureAndTopPSupported
                    ? TEMPERATURE_HELPER_TEXT
                    : 'Temperature is not supported by this model.'
                }
              />
              <BlockTextField
                label="Top P"
                fieldName={`${fieldNamePrefix}.topP`}
                type="number"
                slotProps={{ htmlInput: { min: 0, max: 1, step: 0.1 } }}
                disabled={!isTemperatureAndTopPSupported}
                helperText={
                  isTemperatureAndTopPSupported
                    ? 'Adjusts the diversity of the output by narrowing down the token selection to the most probable set of tokens, summing up to a given probability. A lower value (e.g., 0.5) makes the model more focused, while a higher value (e.g., 0.9) allows for more creative responses. Range 0 to 1.'
                    : 'Top P is not supported by this model.'
                }
              />
              <BlockTextField
                label="Frequency Penalty"
                fieldName={`${fieldNamePrefix}.frequencyPenalty`}
                type="number"
                slotProps={{ htmlInput: { min: -2, max: 2, step: 0.1 } }}
                helperText="Reduces the likelihood of the model repeating the same token or phrase by applying a penalty to frequent tokens. A higher value discourages repetition, promoting more varied responses. Range -2 to 2."
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Accordion variant="elevation" disabled={!isFileContextSupported}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Text variant="heading5">
              {isFileContextSupported ? 'Context' : 'Context not supported by this model'}
            </Text>
          </AccordionSummary>
          {isFileContextSupported && (
            <AccordionDetails>
              <FileReferencesField />
            </AccordionDetails>
          )}
        </Accordion>
      </Stack>
      <Stack gap={3}>
        <Text variant="heading5" bold>
          Structured Outputs
        </Text>
        <ResponseFormatFields fieldNamePrefix={fieldNamePrefix} />
      </Stack>
    </Stack>
  );
}
